import {
  REQUEST_SIMILARITY_UPLOAD,
  ANALYZE_SIMILARITY_UPLOAD,
  SIMILARITY_JOB
} from "./queries";

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes
const MAX_POLL_ERRORS = 3;

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Drives the full similar-tracks-by-upload flow against the backend.
 * @param {ApolloClient} client - Apollo client (from useApolloClient)
 * @param {File} file - the user-selected mp3
 * @returns {Promise<{status: "ready", similarTracks: object[]} | {status: "failed", error: string}>}
 */
export async function runSimilarityUpload(client, file) {
  try {
    const { data: reqData } = await client.mutate({
      mutation: REQUEST_SIMILARITY_UPLOAD,
      variables: { fileName: file.name }
    });
    const { jobId, uploadUrl } = reqData.requestSimilarityUpload;

    const putRes = await fetch(uploadUrl, { method: "PUT", body: file });
    if (!putRes.ok) {
      return { status: "failed", error: `Upload failed (${putRes.status})` };
    }

    await client.mutate({
      mutation: ANALYZE_SIMILARITY_UPLOAD,
      variables: { jobId }
    });

    // poll until the job resolves or we hit the timeout
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    let consecutiveErrors = 0;
    while (Date.now() < deadline) {
      await sleep(POLL_INTERVAL_MS);
      try {
        const { data } = await client.query({
          query: SIMILARITY_JOB,
          fetchPolicy: "network-only",
          variables: { jobId }
        });
        consecutiveErrors = 0;
        const job = data.similarityJob;
        if (job.status === "ready") {
          return { status: "ready", similarTracks: job.similarTracks || [] };
        }
        if (job.status === "failed") {
          return { status: "failed", error: job.error || "Analysis failed" };
        }
      } catch (err) {
        consecutiveErrors += 1;
        if (consecutiveErrors > MAX_POLL_ERRORS) {
          return { status: "failed", error: "Lost connection while analyzing" };
        }
        console.warn("similarityJob poll error (will retry):", err);
      }
    }
    return { status: "failed", error: "Timed out waiting for analysis" };
  } catch (err) {
    console.error("runSimilarityUpload error:", err);
    return { status: "failed", error: err.message || "Upload failed" };
  }
}
